import { useState, useEffect } from "react";
import { getAlertsHistory, markAlertsRead, resolveAlerts } from "../controllers/alertController";

const NotificationsPage = () => {
    const [activeTab, setActiveTab] = useState("all");
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resolvingIds, setResolvingIds] = useState({});

    const getUsername = () => {
        try {
            const stored = localStorage.getItem("user");
            if (stored) {
                const user = JSON.parse(stored);
                return localStorage.getItem("username") || user?.username || user?.userName;
            }
        } catch (e) {}
        return localStorage.getItem("username");
    };

    const fetchAlerts = async () => {
        const username = getUsername();
        if (!username) return;
        setLoading(true);
        try {
            const data = await getAlertsHistory(username);
            setAlerts(data.alerts || []);
        } catch (e) {
            console.error("Failed to fetch alerts:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
    }, []);

    const handleMarkAllRead = async () => {
        const unreadIds = alerts.filter(a => !a.is_read).map(a => a.id);
        if (unreadIds.length === 0) return;
        try {
            await markAlertsRead(unreadIds);
            setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
        } catch (e) {
            console.error("Failed to mark read:", e);
        }
    };

    const handleResolve = async (e, alert) => {
        e.stopPropagation();
        if (!alert?.id || !alert?.meter_id) return;
        const username = getUsername();
        if (!username) return;
        setResolvingIds(prev => ({ ...prev, [alert.id]: true }));
        try {
            await resolveAlerts(username, alert.meter_id, [alert.id]);
            setAlerts(prev =>
                prev.map(a =>
                    a.id === alert.id
                        ? { ...a, is_resolved: true, resolved_reason: 'manual', resolved_at: new Date().toISOString() }
                        : a,
                ),
            );
        } catch (err) {
            console.error("Failed to resolve alert:", err);
        } finally {
            setResolvingIds(prev => {
                const next = { ...prev };
                delete next[alert.id];
                return next;
            });
        }
    };

    const handleMarkOneRead = async (id) => {
        const alert = alerts.find(a => a.id === id);
        if (!alert || alert.is_read) return;
        try {
            await markAlertsRead([id]);
            setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
        } catch (e) {
            console.error("Failed to mark read:", e);
        }
    };

    const formatTime = (timestamp) => {
        const now = new Date();
        const alertTime = new Date(timestamp);
        const diffMs = now - alertTime;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHr = Math.floor(diffMs / 3600000);
        const diffDay = Math.floor(diffMs / 86400000);

        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        if (diffHr < 24) return `${diffHr} hr ago`;
        if (diffDay < 7) return `${diffDay} day ago`;
        return alertTime.toLocaleDateString();
    };

    const getSeverityColor = (alertType) => {
        if (alertType === 'power_exceeded') return '#ff4d4f';
        if (alertType === 'outside_hours') return '#faad14';
        if (alertType === 'inactive_during_hours') return '#fa8c16';
        return '#1890ff';
    };

    const getSeverityLabel = (alertType) => {
        if (alertType === 'power_exceeded') return 'High';
        if (alertType === 'outside_hours') return 'Warning';
        if (alertType === 'inactive_during_hours') return 'Inactive';
        return 'Info';
    };

    const getResolvedLabel = (reason) => {
        const labels = {
            machine_off: 'Machine turned off',
            machine_on: 'Machine turned on',
            hours_started: 'Back in working hours',
            hours_ended: 'Working hours ended',
            power_normal: 'Power back to normal',
            manual: 'Resolved by user',
        };
        return labels[reason] || reason;
    };

    const getAlertTitle = (alertType) => {
        if (alertType === 'power_exceeded') return 'Power Exceeded';
        if (alertType === 'outside_hours') return 'Outside Working Hours';
        if (alertType === 'inactive_during_hours') return 'Inactive During Hours';
        return 'Alert';
    };

    const filteredItems = activeTab === "all"
        ? alerts
        : alerts.filter(item => item.alert_type === activeTab);

    const unreadCount = alerts.filter(a => !a.is_read).length;

    return (
        <div style={styles.container}>
            <div style={styles.titleRow}>
                <h1 style={styles.pageTitle}>Alerts</h1>
                {unreadCount > 0 && (
                    <button style={styles.markAllBtn} onClick={handleMarkAllRead}>
                        Mark all as read ({unreadCount})
                    </button>
                )}
            </div>

            <div style={styles.card}>
                <div style={styles.tabs}>
                    <button
                        style={{ ...styles.tab, borderBottom: activeTab === "all" ? "2px solid #1BA08E" : "none", color: activeTab === "all" ? "#1BA08E" : "#666" }}
                        onClick={() => setActiveTab("all")}
                    >
                        All ({alerts.length})
                    </button>
                    <button
                        style={{ ...styles.tab, borderBottom: activeTab === "power_exceeded" ? "2px solid #1BA08E" : "none", color: activeTab === "power_exceeded" ? "#1BA08E" : "#666" }}
                        onClick={() => setActiveTab("power_exceeded")}
                    >
                        Power Exceeded ({alerts.filter(a => a.alert_type === 'power_exceeded').length})
                    </button>
                    <button
                        style={{ ...styles.tab, borderBottom: activeTab === "outside_hours" ? "2px solid #1BA08E" : "none", color: activeTab === "outside_hours" ? "#1BA08E" : "#666" }}
                        onClick={() => setActiveTab("outside_hours")}
                    >
                        Outside Hours ({alerts.filter(a => a.alert_type === 'outside_hours').length})
                    </button>
                    <button
                        style={{ ...styles.tab, borderBottom: activeTab === "inactive_during_hours" ? "2px solid #1BA08E" : "none", color: activeTab === "inactive_during_hours" ? "#1BA08E" : "#666" }}
                        onClick={() => setActiveTab("inactive_during_hours")}
                    >
                        Inactive ({alerts.filter(a => a.alert_type === 'inactive_during_hours').length})
                    </button>
                </div>

                <div style={styles.list}>
                    {loading ? (
                        <div style={styles.emptyState}>Loading alerts...</div>
                    ) : filteredItems.length === 0 ? (
                        <div style={styles.emptyState}>No alerts found.</div>
                    ) : (
                        filteredItems.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    ...styles.item,
                                    backgroundColor: item.is_resolved ? '#fafafa' : item.is_read ? '#fff' : '#f6ffed',
                                    opacity: item.is_resolved ? 0.75 : 1,
                                }}
                                onClick={() => handleMarkOneRead(item.id)}
                            >
                                <div style={styles.iconWrapper}>
                                    <div style={{
                                        ...styles.icon,
                                        backgroundColor: item.is_resolved ? '#f0f0f0' : item.alert_type === 'power_exceeded' ? '#FFF1F0' : item.alert_type === 'inactive_during_hours' ? '#FFF7E6' : '#FFF7E6',
                                        color: item.is_resolved ? '#999' : item.alert_type === 'power_exceeded' ? '#F5222D' : item.alert_type === 'inactive_during_hours' ? '#FA8C16' : '#FA8C16',
                                    }}>
                                        {item.alert_type === 'power_exceeded' ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>
                                        ) : item.alert_type === 'inactive_during_hours' ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                        )}
                                    </div>
                                </div>
                                <div style={styles.content}>
                                    <div style={styles.header}>
                                        <h3 style={styles.title}>{getAlertTitle(item.alert_type)}</h3>
                                        {item.is_resolved ? (
                                            <span style={styles.resolvedBadge}>Resolved</span>
                                        ) : (
                                            <span style={{
                                                ...styles.badge,
                                                backgroundColor: getSeverityColor(item.alert_type),
                                            }}>
                                                {getSeverityLabel(item.alert_type)}
                                            </span>
                                        )}
                                        {!item.is_read && !item.is_resolved && <span style={styles.unreadDot} />}
                                        <span style={styles.time}>{formatTime(item.timestamp)}</span>
                                    </div>
                                    <p style={styles.message}>{item.message}</p>
                                    {item.is_resolved && item.resolved_reason && (
                                        <p style={styles.resolvedReason}>Reason: {getResolvedLabel(item.resolved_reason)}</p>
                                    )}
                                    {item.is_resolved && item.resolved_at && (
                                        <p style={styles.resolvedTime}>Resolved {formatTime(item.resolved_at)}</p>
                                    )}
                                    {!item.is_resolved && (
                                        <div style={{ marginTop: 8 }}>
                                            <button
                                                type="button"
                                                onClick={(e) => handleResolve(e, item)}
                                                disabled={!!resolvingIds[item.id]}
                                                style={{
                                                    ...styles.resolveBtn,
                                                    opacity: resolvingIds[item.id] ? 0.5 : 1,
                                                    cursor: resolvingIds[item.id] ? 'not-allowed' : 'pointer',
                                                }}
                                            >
                                                {resolvingIds[item.id] ? 'Resolving...' : 'Mark as Resolved'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        padding: "24px",
        maxWidth: "1000px",
        margin: "0 auto",
    },
    titleRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
    },
    pageTitle: {
        fontSize: "24px",
        fontWeight: "bold",
        color: "#333",
        margin: 0,
    },
    markAllBtn: {
        border: "1.5px solid #1BA08E",
        backgroundColor: "#fff",
        color: "#1BA08E",
        cursor: "pointer",
        fontWeight: 600,
        fontSize: "13px",
        padding: "8px 16px",
        borderRadius: "6px",
    },
    card: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
    },
    tabs: {
        display: "flex",
        borderBottom: "1px solid #f0f0f0",
        padding: "0 16px",
    },
    tab: {
        padding: "16px 24px",
        fontSize: "14px",
        fontWeight: "600",
        background: "none",
        border: "none",
        cursor: "pointer",
        marginRight: "8px",
        transition: "color 0.3s",
    },
    list: {
        padding: "0",
    },
    item: {
        display: "flex",
        padding: "20px 24px",
        borderBottom: "1px solid #f0f0f0",
        transition: "background-color 0.2s",
        cursor: "pointer",
        alignItems: "flex-start",
    },
    iconWrapper: {
        marginRight: "16px",
        marginTop: "4px",
    },
    icon: {
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    content: {
        flex: 1,
    },
    header: {
        display: "flex",
        alignItems: "center",
        marginBottom: "4px",
        flexWrap: "wrap",
        gap: "8px",
    },
    title: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#262626",
        margin: 0,
    },
    badge: {
        fontSize: "11px",
        padding: "2px 8px",
        borderRadius: "10px",
        color: "#fff",
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    unreadDot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: "#1BA08E",
    },
    time: {
        fontSize: "12px",
        color: "#999",
        marginLeft: "auto",
    },
    message: {
        fontSize: "14px",
        color: "#666",
        margin: "4px 0 0 0",
        lineHeight: "1.5",
    },
    resolvedBadge: {
        fontSize: "11px",
        padding: "2px 8px",
        borderRadius: "10px",
        color: "#52c41a",
        fontWeight: "bold",
        textTransform: "uppercase",
        backgroundColor: "#f6ffed",
        border: "1px solid #b7eb8f",
    },
    resolvedReason: {
        fontSize: "13px",
        color: "#52c41a",
        fontWeight: 600,
        margin: "4px 0 0 0",
    },
    resolvedTime: {
        fontSize: "12px",
        color: "#999",
        margin: "4px 0 0 0",
        fontStyle: "italic",
    },
    resolveBtn: {
        background: "#1BA08E",
        color: "#fff",
        border: "none",
        borderRadius: 6,
        padding: "6px 14px",
        fontSize: 12,
        fontWeight: 600,
    },
    emptyState: {
        padding: "40px",
        textAlign: "center",
        color: "#999",
    },
};

export default NotificationsPage;
